import nav from './nav.json';
import { LEAGUES, type LeagueId } from './leagues';
import { GENRES } from './genres';
import { MENU } from './competitions';

export type NavChild = { label: string; to: string; note?: { n: number; unit: 'dates' | 'games' } };
export type NavSection = {
  label: string;
  to: string;
  /** what the second level is called, so the panel can say it */
  childLabel?: string;
  children: NavChild[];
  /** a second, shorter list under the first: the competition, tour or show pages this build wrote (src/data/competitions.ts) */
  extra?: { label: string; children: NavChild[] };
};

/** Shape of src/data/nav.json (written by scripts/gen-catalog.mjs with the catalog). */
export interface NavData {
  updated: string;
  /** ISO time the catalog was generated — the "prices recorded" stamp on event pages */
  generatedAt?: string;
  total: number;
  /** distinct cities / venues with upcoming events (cities[] is capped) */
  cityCount?: number;
  venueCount?: number;
  /** distinct countries with upcoming events, and the busiest of them (scripts/hubs.mjs) */
  countryCount?: number;
  countries?: { cc: string; n: number }[];
  /** events whose prices are recorded daily (the catalog plus indexed edge pages) */
  tracked?: number;
  categories: { name: string; n: number }[];
  kinds: { name: string; n: number }[];
  genres: Record<string, { name: string; n: number }[]>;
  cities: { name: string; n: number; slug: string; from: number | null }[];
  acts: Record<string, { name: string; slug: string; n: number }[]>;
}

export const NAV_DATA = nav as NavData;

/** League rows for the Sports panel: the queried SeatGeek kind is the league. */
const leagueChildren = (min = 8): NavChild[] =>
  (Object.keys(LEAGUES) as LeagueId[])
    .map((id) => ({ id, count: NAV_DATA.kinds.find((k) => k.name === id)?.n ?? 0 }))
    .filter((l) => l.count >= min)
    .sort((a, b) => b.count - a.count)
    .map((l) => ({ label: LEAGUES[l.id].name, to: `/league/${l.id}`, note: { n: l.count, unit: 'games' as const } }));

const actChildren = (category: string, limit: number): NavChild[] =>
  (NAV_DATA.acts[category] ?? []).slice(0, limit).map((a) => ({ label: a.name, to: `/artist/${a.slug}`, note: { n: a.n, unit: 'dates' as const } }));

/** Up to five pages of one kind, soonest first (shared/competition-index.mjs menuFile, written by the build): none until it wrote any. */
const compExtra = (group: 'sports' | 'theatre' | 'music', label: string): Pick<NavSection, 'extra'> => {
  const children = MENU[group].map((e) => ({ label: e.title, to: `/tournament/${e.id}` }));
  return children.length ? { extra: { label, children } } : {};
};

/**
 * The masthead menu. "Sports" on its own tells a fan nothing — they came for
 * the NFL, or for the Cowboys. So each category opens onto the level below
 * it: leagues for sports (then clubs, on the league page), and the busiest
 * acts for concerts, comedy and theater.
 *
 * Built from nav.json, which the catalog build writes alongside the catalog:
 * counts are real, every link points at a page with events, and the browser
 * needs no catalog slice to draw the chrome.
 */
export const NAV: NavSection[] = [
  { label: 'Concerts', to: '/music', childLabel: 'By genre', children: GENRES.Music.slice(0, 9).map((g) => ({ label: g.label, to: `/music/${g.slug}`, note: { n: g.n, unit: 'dates' as const } })), ...compExtra('music', 'Tours and residencies') },
  { label: 'Sports', to: '/sports', childLabel: 'Leagues', children: leagueChildren(), ...compExtra('sports', 'Competitions') },
  { label: 'Comedy', to: '/comedy', childLabel: 'Popular comedians', children: actChildren('Comedy', 8) },
  { label: 'Theater', to: '/theater', childLabel: 'What kind', children: [...GENRES.Theater.slice(0, 6).map((g) => ({ label: g.label, to: `/theater/${g.slug}`, note: { n: g.n, unit: 'dates' as const } })), ...actChildren('Theater', 3)], ...compExtra('theatre', 'West End shows') },
  { label: 'This Weekend', to: '/this-weekend', children: [] },
];
